import React from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Typography,
  Box,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SchoolIcon from '@mui/icons-material/School';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import { useAuth } from '../../hooks/useAuth';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { PageHelpDialog, type PageHelpTopic } from '../help/PageHelpDialog';
import { docsUrl, TUTORIALS_PATH } from '../../lib/docsUrls';
import { TrialPill } from '../billing/TrialPill';

// Section of a step-by-step tutorial on the docs site (anchors are the docs site's heading ids).
const tutorial = (slug: string, anchor?: string) => ({ path: `${TUTORIALS_PATH}/${slug}`, anchor });

interface TopBarProps {
  onMenuClick: () => void;
}

export function TopBar({ onMenuClick }: TopBarProps) {
  const { user, isEmbedded, logout } = useAuth();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [helpOpen, setHelpOpen] = React.useState(false);

  const topic = React.useMemo<PageHelpTopic>(() => {
    const path = location.pathname;
    if (path === '/getting-started') return { title: 'Getting started', path: '/docs/quick-start', anchor: '5-create-your-first-survey', tutorial: tutorial('create-and-publish') };
    if (path === '/intelligence') return { title: 'Insights', path: '/docs/introduction', anchor: 'key-capabilities' };
    if (path === '/surveys/new/ai') return { title: 'AI survey designer', path: '/docs/guides/ai-designer', tutorial: tutorial('create-and-publish', 'part-1-start-a-new-survey') };
    if (path === '/ai-usage') return { title: 'AI usage', path: '/docs/api/ai' };
    if (/^\/surveys\/[^/]+\/edit$/.test(path)) return { title: 'Survey builder', path: '/docs/guides/create-survey', tutorial: tutorial('create-and-publish', 'part-2-add-pages-and-questions') };
    if (/^\/surveys\/[^/]+\/analytics$/.test(path)) return { title: 'Analytics', path: '/docs/api/responses', anchor: 'analytics-aggregates', tutorial: tutorial('results-and-export', 'part-2-read-the-analytics') };
    if (/^\/surveys\/[^/]+\/distributions(?:\/[^/]+)?$/.test(path)) return { title: 'Email invitations', path: '/docs/guides/share-and-embed-how-to' };
    if (/^\/surveys\/[^/]+\/responses$/.test(path)) return { title: 'Responses', path: '/docs/api/responses', anchor: 'list-responses', tutorial: tutorial('results-and-export', 'part-3-look-at-individual-responses') };
    if (path === '/surveys') return { title: 'Surveys', path: '/docs/guides/create-survey', tutorial: tutorial('create-and-publish', 'part-1-start-a-new-survey') };
    if (path === '/templates') return { title: 'Templates', path: '/docs/guides/create-survey', anchor: 'create-from-a-template', tutorial: tutorial('create-and-publish', 'part-1-start-a-new-survey') };
    if (path === '/profile') return { title: 'Profile', path: '/docs/admin/members' };
    if (path === '/settings') return { title: 'Workspace settings', path: '/docs/admin/workspaces', anchor: 'workspace-settings', tutorial: tutorial('workspace-and-integrations', 'part-1-name-and-brand-the-workspace') };
    if (path === '/api-keys') return { title: 'API keys', path: '/docs/admin/api-keys', tutorial: tutorial('workspace-and-integrations', 'part-4-create-an-api-key-for-the-website') };
    if (path === '/embed-preview') return { title: 'Preview in your product', path: '/docs/guides/embedding' };
    if (path === '/embed-integration' || path.startsWith('/embedded/')) return { title: 'Embedding', path: '/docs/guides/embedding', anchor: 'integration-wizard', tutorial: tutorial('workspace-and-integrations', 'part-5-embed-responsedock-in-your-website') };
    if (path === '/connected-apps') return { title: 'Connected apps', path: '/docs/guides/slack-teams', anchor: 'connect-slack' };
    if (path === '/webhooks') return { title: 'Webhooks', path: '/docs/guides/webhooks', tutorial: tutorial('workspace-and-integrations', 'part-7-send-new-responses-to-your-other-tools-with-webhooks') };
    if (path === '/triggers') return { title: 'Audience targeting', path: '/docs/guides/triggers', tutorial: tutorial('workspace-and-integrations', 'part-6-ask-guests-at-the-right-moment-with-audience-targeting') };
    if (path === '/system/users') return { title: 'System users', path: '/docs/admin/members' };
    if (path === '/system/surveys') return { title: 'All surveys', path: '/docs/admin/members' };
    return { title: 'Dashboard', path: '/docs/introduction', anchor: 'key-capabilities', tutorial: tutorial('results-and-export', 'part-1-start-from-the-dashboard') };
  }, [location.pathname]);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary',
      }}
    >
      <Toolbar>
        <IconButton
          edge="start"
          aria-label="Open navigation"
          onClick={onMenuClick}
          sx={{ mr: 1, display: { sm: 'none' } }}
        >
          <MenuIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
          {!isEmbedded && <TrialPill />}
        </Box>
        <Tooltip title={`Help: ${topic.title}`}>
          <IconButton aria-label={`Open ${topic.title} documentation`} onClick={() => setHelpOpen(true)} size="small" sx={{ mr: 1 }}>
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
        {user && (
          <>
            <Tooltip title={user.name}>
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
                <Avatar
                  src={user.avatarUrl ?? undefined}
                  alt={user.name}
                  sx={{ width: 32, height: 32 }}
                >
                  {user.name[0]}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              <MenuItem disabled>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{user.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                </Box>
              </MenuItem>
              {!isEmbedded && (
                <MenuItem component={RouterLink} to="/profile" onClick={() => setAnchorEl(null)}>
                  <PersonOutlineIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                  Profile
                </MenuItem>
              )}
              <MenuItem component="a" href={docsUrl(TUTORIALS_PATH)} target="_blank" rel="noopener" onClick={() => setAnchorEl(null)}>
                <SchoolIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                Tutorials
              </MenuItem>
              <MenuItem component="a" href={docsUrl('/docs/introduction')} target="_blank" rel="noopener" onClick={() => setAnchorEl(null)}>
                <MenuBookIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                Documentation
              </MenuItem>
              <MenuItem onClick={() => { void logout(); setAnchorEl(null); }}>
                Sign out
              </MenuItem>
            </Menu>
          </>
        )}
      </Toolbar>
      <PageHelpDialog open={helpOpen} topic={topic} onClose={() => setHelpOpen(false)} />
    </AppBar>
  );
}
